import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Landmark, Lock, Globe2, Quote } from "lucide-react";
import Logo from "@/components/Logo";
import FlowBackdrop from "@/components/illustrations/FlowBackdrop";

const ASSURANCES = [
  { icon: ShieldCheck, text: "SEBI-registered advisory partners" },
  { icon: Landmark, text: "Funds move only via your own NRE / NRO accounts" },
  { icon: Lock, text: "AES-256 at rest · TLS 1.3 in transit" },
  { icon: Globe2, text: "Onboarding across 32 countries" },
];

const STATS = [
  { value: "2,400+", label: "NRIs served" },
  { value: "₹680 Cr", label: "Assets advised" },
  { value: "5 days", label: "Avg. onboarding" },
];

export function AuthShell({ children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-[1fr_0.9fr] bg-[hsl(var(--zt-beige))]">
      {/* ── Form column ─────────────────────────────────────────── */}
      <div className="relative flex flex-col px-6 py-8 sm:px-10 lg:px-16 xl:px-24">
        <div aria-hidden className="absolute inset-0 dot-grid opacity-40 pointer-events-none" />

        {/* Top bar */}
        <div className="relative flex items-center justify-between">
          <Logo size="h-12" to="/" className="transition-transform duration-300 hover:scale-[1.02]" />
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 font-heading text-[12.5px] font-medium text-[hsl(var(--zt-muted))] hover:text-[hsl(var(--zt-teal))] transition-colors duration-200"
          >
            <ArrowLeft size={14} /> Back to site
          </Link>
        </div>

        {/* Form */}
        <div className="relative flex-1 flex items-center justify-center py-10">
          <div className="w-full max-w-[420px] space-y-6 animate-fade-up">{children}</div>
        </div>

        {/* Footer note */}
        <p className="relative font-body text-[11.5px] text-[hsl(var(--zt-muted))] text-center lg:text-left">
          Zenstrust never takes custody of your assets. © {new Date().getFullYear()} Zenstrust Pvt. Ltd.
        </p>
      </div>

      {/* ── Visual column ───────────────────────────────────────── */}
      <div className="hidden lg:flex relative overflow-hidden bg-[hsl(var(--zt-teal))] flex-col justify-between p-12 xl:p-14">
        <div aria-hidden className="absolute inset-0 dot-grid-inverse opacity-30 pointer-events-none" />
        <FlowBackdrop variant="dark" pattern="z" className="opacity-60" />
        <div
          aria-hidden
          className="absolute top-[-15%] right-[-20%] w-[520px] h-[520px] rounded-full opacity-[0.18] blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, #4F63F0 0%, transparent 65%)" }}
        />

        {/* Headline */}
        <div className="relative max-w-md">
          <span className="gold-rule" />
          <h2 className="font-display text-[2rem] xl:text-[2.4rem] leading-[1.12] text-white mt-5">
            The cross-border wealth desk for the{" "}
            <span className="text-[hsl(var(--zt-gold-soft))]">global Indian</span>.
          </h2>
          <p className="font-body text-[14px] text-white/60 leading-relaxed mt-4">
            AI-guided portfolios, registered advisors and compliance handled end to end — from wherever
            you live, into the India rails that matter.
          </p>
        </div>

        {/* Testimonial */}
        <div className="relative rounded-2xl border border-white/10 bg-white/[0.05] backdrop-blur-sm p-6 max-w-md">
          <Quote size={18} className="text-[hsl(var(--zt-gold-soft))]" />
          <p className="font-body text-[15px] leading-relaxed text-white/85 mt-3">
            Zenstrust gave me the clarity I'd been searching for since I left India in 2009. One team,
            one dashboard, no chasing paperwork across time zones.
          </p>
          <div className="mt-5 flex items-center gap-3 border-t border-white/10 pt-4">
            <div className="w-9 h-9 rounded-full bg-[hsl(var(--zt-gold))]/25 border border-[hsl(var(--zt-gold))]/40 grid place-items-center font-heading text-[13px] font-bold text-white">
              R
            </div>
            <div>
              <p className="font-heading text-[13px] font-semibold text-white">Ravi Menon</p>
              <p className="font-heading text-[11px] text-white/50 font-medium mt-0.5">
                London · Member since 2023
              </p>
            </div>
          </div>
        </div>

        {/* Assurances + stats */}
        <div className="relative space-y-6 max-w-md">
          <ul className="space-y-2.5">
            {ASSURANCES.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-2.5">
                <Icon size={14} className="text-[hsl(var(--zt-sage))] flex-shrink-0" />
                <span className="font-heading text-[12.5px] text-white/70 font-medium">{text}</span>
              </li>
            ))}
          </ul>

          <dl className="grid grid-cols-3 gap-4 pt-5 border-t border-white/10">
            {STATS.map((s) => (
              <div key={s.label}>
                <dd className="font-numeric text-[1.3rem] text-white font-semibold">{s.value}</dd>
                <dt className="font-heading text-[10.5px] text-white/45 font-medium mt-1">{s.label}</dt>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}

export function AuthHeading({ eyebrow, title, accent, subtitle }) {
  return (
    <div className="space-y-3">
      <span className="eyebrow">{eyebrow}</span>
      <h1 className="font-display text-[1.9rem] sm:text-[2.15rem] leading-[1.15] text-[hsl(var(--zt-teal))]">
        {title} {accent && <span className="text-[hsl(var(--zt-gold))]">{accent}</span>}
      </h1>
      {subtitle && (
        <p className="font-body text-[14.5px] text-[hsl(var(--zt-charcoal))]/80 leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}

export function AuthField({ label, hint, children }) {
  return (
    <label className="block space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="font-heading text-[12.5px] font-semibold text-[hsl(var(--zt-teal))]">{label}</span>
        {hint}
      </div>
      {children}
    </label>
  );
}

export function AuthSubmit({ busy, children, busyLabel, ...rest }) {
  return (
    <button
      type="submit"
      disabled={busy}
      className="w-full bg-[hsl(var(--zt-gold))] text-white hover:bg-[hsl(var(--zt-gold))]/92 disabled:opacity-60 rounded-xl h-12 font-heading text-sm font-semibold tracking-tight flex items-center justify-center gap-2 elev-2 transition-all duration-200 hover:-translate-y-0.5 disabled:hover:translate-y-0"
      {...rest}
    >
      {busy ? busyLabel : children}
    </button>
  );
}

export function Divider() {
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="flex-1 h-px bg-[hsl(var(--zt-beige-deep))]" />
      <span className="font-heading text-[11px] text-[hsl(var(--zt-muted))] font-semibold">or</span>
      <div className="flex-1 h-px bg-[hsl(var(--zt-beige-deep))]" />
    </div>
  );
}

export function GoogleButton({ onClick, label = "Continue with Google", ...rest }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full bg-white border border-[hsl(var(--zt-teal))]/12 hover:border-[hsl(var(--zt-teal))]/25 text-[hsl(var(--zt-teal))] rounded-xl h-12 font-heading text-sm font-semibold tracking-tight flex items-center justify-center gap-2.5 elev-1 transition-all duration-200 hover:-translate-y-0.5"
      {...rest}
    >
      <GoogleMark /> {label}
    </button>
  );
}

export function GoogleMark() {
  return (
    <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true" className="flex-shrink-0">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 6.5 29.4 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 6.5 29.4 4.5 24 4.5 16.4 4.5 9.8 8.6 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 43.5c5.3 0 10.1-2 13.7-5.3l-6.3-5.2c-2 1.4-4.6 2.3-7.4 2.3-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.6 39.4 16.3 43.5 24 43.5z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.5l6.3 5.2C41.6 35 43.5 30 43.5 24c0-1.2-.1-2.4-.4-3.5z" />
    </svg>
  );
}
